import type { BaseTranslation } from "../i18n-types.js";

const en = {
  pay: "Pay",
  received: "Receive",
  home: {
    payLimit: "Min {minimum} {currency}, max {maximum} {currency}",
    previewOrder: "Preview Order",
    selectAToken: "Select a token",
    selectCurrency: "Select a currency",
    selectCard: "Select a card",
    addDebitOrCreditCard: "Add Debit or Credit Card",
    enterAddress: "Enter wallet address",
    address: "Wallet address",
    inputCard: {
      cardNumber: "Card Number",
      expiryDate: "Expiry Date",
      securityCode: "Security Code",
      holder: "Cardholder Name",
      action: "Add card",
    },
    buy: "Buy {symbol:string}",
    sell: "Sell {symbol:string}",
    swap: "Swap",
    portal: {
      title: "Trade",
      swap: "Swap",
      swapDesc: "Instant swap at market price",
      swapHelp: "How to swap",
      limit: "Limit Order",
      limitDesc: "Fill at your target price",
      perps: "Futures",
      perpsDesc: "Go long or short with leverage",
      fiat: "Fiat ⇄ USDT Guides",
      buyDesc: "Fiat to USDT",
      sellDesc: "USDT to fiat",
    },
  },
  bankTransferInstruction: {
    title: "Bank Transfer Instructions",
    menuName: "Bank Transfer",
    desc: [
      "Please confirm that the name on your bank account matches the KYC information.",
      "Please copy the information above to your bank transfer slip.",
    ],
    sections: {
      beneficiary: {
        title: "Beneficiary",
        items: {
          remarks: {
            title: "Remarks",
            desc: "This code is used to identify your transfer. Please be sure to include it.",
          },
          IBAN: "IBAN",
          accountName: "Account Name",
          accountNumber: "Account Number",
          address: "Address",
        },
      },
      beneficiaryBank: {
        title: "Beneficiary Bank",
        items: {
          BIC_SWIFTCode: "BIC / SWIFT Code",
          bankName: "Bank Name",
          bankAddress: "Bank Address",
          bankCity: "Bank City",
          bankCountry: "Bank Country",
        },
      },
    },
  },
  payment: {
    method: "Payment Method",
    totalToken: "Token Amount",
    price: "Price",
    feeByGateway: "Fee by Gateway",
    feeByMixin: "Fee by Mixin",
    feeByNetwork: "Fee by Network",
    destination: "Destination",
    totalAmount: "Total Amount",
    tips: "Your bank may charge additional fees. Please contact your bank for details.",
  },
  login: {
    action: "Log in with Mixin",
    actionWithPreorder: "Authorize",
    actionWithEmail: "Continue with Email",
    email: {
      title: "Log in to Mixin Route",
      actionWithSend: "Send code",
      verificate: "Verify code",
      otherLoginOptions: "← Other login options",

      emailPlaceholder: "Email address",
      securityCodePlaceholder: "Security Code",

      verificateCodeTips:
        "Please enter the verification code sent to {email:string}.",
    },
  },
  order: {
    detail: "Order Details",
    payment: {
      fetching: "Fetching order...",
      failed: "Payment failed",
      paying: "Paying",
      success: "Payment completed",
      successDesc:
        "Once payment is complete, the token will be transferred directly to your wallet.",
      expired: "Price expired",
    },
    status: {
      created: "Created",
      paying: "Pending",
      success: "Success",
      failed: "Failed",
      returnTo: "Return to {name:string}",
    },
    useLatestPrice: "Use the latest price",
  },
  orders: {
    title: "Orders",
    boughtSome: "Bought {symbol:string}",
  },
  card: {
    visa: "Visa",
    mastercard: "Mastercard",
  },
  kyc: "KYC",
  copyKycLink: "Copy KYC Link",
  status: "Status",
  name: "Mixin Route",
  slogan: "Instantly buy cryptocurrency in your wallet",
  delete: "Delete",
  done: "Done",
  cancel: "Cancel",
  retry: "Retry",
  back: "Back",
  copied: "Copied",
  error: {
    10601: "The card has expired.",
    10602: "The price quote has expired.",
    10604: "Unsupported card, please try another card.",
    10605: "This card is being used at the same time.",
    10606: "Too many attempts, please try again later.",
    10607: "Too many cards added.",
    10608: "Cardholder name does not match your name.",
    10609: "Session expired.",
    10610: "Country mismatch. Please contact customer service.",
    20111: "Email rate limit exceeded. Please try again later.",
    20113: "Invalid verification code.",
    20114: "Verification code expired.",
    20115: "Invalid wallet address.",
    // Insufficient amount
    20116: "The price quote has expired.",
    addCard: "Failed to add the card.",
    quote: "Failed to get the latest quote. The price may have expired.",
    auth: "Authentication failed. Details: {error:string}",
    kycBlocked:
      "Identity verification failed. Please contact customer service.",

    pay: "Payment failed. Please try again.",

    unknown: "Unknown error. Please try again later.",
    network: "Network error. Please try again later.",

    notFound: "Not Found",
    timeout: "Network timed out. Please try again later.",
    params: "Invalid parameters.",

    copy: "Failed to copy to clipboard.",
  },
  form: {
    required: "This field is required.",
    email: "Invalid email address.",
  },
  onramp: {
    poweredBy: "Powered by Coinbase",
    loginTitle: "Log in to continue",
    loginDesc: "Login now to buy cryptocurrency with Apple Pay.",
    continueWithApple: " Continue with Apple",
    agreementText:
      "By continuing, you agree to the <>Privacy Policy</> and <>Terms of Service</>.",
    missingParameters: "Missing parameters",
    assetNotFound: "Asset not found",
    orderConfirm: "Confirm Order",
    transactionError:
      "There was an issue processing this transaction. Please try again later.",
    paymentSuccess:
      "Payment successful. Your {amount:string} {symbol:string} will be transferred directly to your Mixin wallet shortly. Please be patient.",
    tokenAmount: "Token Amount",
    bankFeeTips:
      "Your bank may charge additional fees. Please contact your bank for details.",
    processing: "Processing",
    applePayPurchase: "Apple Pay Purchase",
    creatingOrder: "Creating order…",
    loadingPayment: "Please wait, the payment page is loading…",
    loadingPaymentData: "Fetching payment data…",
    loadingPaymentDomReady: "Please wait, the payment page is initializing…",
    waitingPaymentReady: "Please wait, connecting to the payment page…",
    processingPayment: "Processing payment…",
    waitingToCreateOrder: "Waiting to create order…",
    orderCreationFailed: "Failed to create order",
    paymentLoadFailed: "Failed to load the payment page",
    paymentCommitFailed: "Failed to submit the payment",
    paymentFailed: "Payment failed",
    failedBillingHint: "Please make sure your Apple Pay billing information is correct.",
    useOtherPaymentMethod: "Use another payment method",
    limitReached:
      "This transaction has reached the limit. Complete verification to increase your limit or use another payment method.",
    upgradeLimit: "Increase limit",
    startVerification: "Start verification",
    cancelVerification: "Cancel",
    verifyIdentity: "Verify identity",
    verifyIdentityPending: "Identity verifying",
    verifyingIdentityDesc:
      "Your identity is being verified. We will send you a message once your verification is complete.",
    gotIt: "Got it",
    last4Ssn: "Last 4 SSN",
    birthOfDate: "Birth of date",
    verificationGuideDesc:
      "For compliance and security reasons, increasing your limit requires verifying your Social Security Number and date of birth. After verification:",
    verificationGuideItem1: "Your weekly limit increases to 2,500 USD.",
    verificationGuideItem2: "Your purchase count increases to unlimited.",
    verificationFormDesc:
      "For compliance and security reasons, we need to verify the last 4 digits of your Social Security Number (SSN) and your date of birth.",
    formErrorSsnLast4:
      "Please enter the last 4 digits of your Social Security Number.",
    formErrorBirthDate: "Please select your date of birth.",
    limitUpgradeFailed:
      "Failed to submit identity verification. Please try again later.",
    errorCodes: {
      ERROR_CODE_INIT:
        "Payment link is invalid or expired. Please recreate the order.",
      ERROR_CODE_GUEST_APPLE_PAY_NOT_SUPPORTED:
        "Apple Pay is not supported on this browser/device.",
      ERROR_CODE_GUEST_APPLE_PAY_NOT_SETUP:
        "Apple Pay is not set up on this device. Please set up Apple Pay and try again.",
      ERROR_CODE_GUEST_CARD_SOFT_DECLINED:
        "Payment was declined by the bank. Please try another card.",
      ERROR_CODE_GUEST_INVALID_CARD:
        "Invalid card or billing address. Please check and try again.",
      ERROR_CODE_GUEST_CARD_INSUFFICIENT_BALANCE:
        "Insufficient balance on this card.",
      ERROR_CODE_GUEST_CARD_HARD_DECLINED:
        "Payment was declined by the issuing bank. Please try another card.",
      ERROR_CODE_GUEST_CARD_RISK_DECLINED:
        "Payment was blocked due to risk rules.",
      ERROR_CODE_GUEST_REGION_MISMATCH: "This region is not supported.",
      ERROR_CODE_GUEST_PERMISSION_DENIED:
        "This account is blocked from using the on-ramp.",
      ERROR_CODE_GUEST_CARD_PREPAID_DECLINED:
        "Prepaid debit cards are not supported. Please use another card.",
      ERROR_CODE_GUEST_TRANSACTION_LIMIT:
        "This exceeds the weekly transaction limit.",
      ERROR_CODE_GUEST_TRANSACTION_COUNT:
        "This exceeds the lifetime transaction count limit.",
      ERROR_CODE_GUEST_TRANSACTION_BUY_FAILED:
        "We were unable to complete the crypto purchase. Your card will not be charged.",
      ERROR_CODE_GUEST_TRANSACTION_SEND_FAILED:
        "We were unable to send funds to the destination. The card will be refunded.",
      ERROR_CODE_GUEST_TRANSACTION_TRANSACTION_FAILED:
        "An internal error occurred while processing the transaction.",
      ERROR_CODE_GUEST_TRANSACTION_AVS_VALIDATION_FAILED:
        "Billing address validation failed. Please verify the address with your bank.",
    },
  },
} satisfies BaseTranslation;

export default en;
